import {Gender, INonMember} from 'types/nonMember';
import PersonalDetailsEditFields from './PersonalDetailsEditFields';
import React, {Dispatch, FC, SetStateAction, useEffect, useState} from 'react';
import {ModalViewEnum} from '.';
import {onChange as onChangeHelper} from 'utils/onChangeHandler';
import Controls from 'controls';
import moment from 'moment';
import {PublicEventType} from 'enums/public_event_type';
import {useDispatch} from 'react-redux';
import {validateEmail, validatePhone} from 'utils/valdiators';
import actions from 'store/actions';
import {StatusType} from 'store/status/types';
import site from 'settings/site';

interface EditNonMemberProps {
  lead: Partial<INonMember>;
  setView: Dispatch<SetStateAction<ModalViewEnum>>;
  onCancel: () => void;
  setLead: React.Dispatch<React.SetStateAction<Partial<INonMember>>>;
  type: PublicEventType;
}

export const EditNonMember: FC<EditNonMemberProps> = ({setView, onCancel, lead, setLead, type}) => {
  const dispatch = useDispatch();

  const [gender, setGender] = useState<Gender | undefined>(lead.personalDetails?.gender);
  const [age, setAge] = useState<number>(lead.personalDetails?.age ?? 0);

  const [guardianMailingAddress, setGuardianMailingAddress] = useState<string | undefined>(
    lead.personalDetails?.guardianMailingAddress
  );
  const [guardianEmail, setGuardianEmail] = useState<string | undefined>(lead.personalDetails?.guardianEmail);
  const [guardianHomeNumber, setGuardianHomeNumber] = useState<string | undefined>(
    lead.personalDetails?.guardianHomeNumber
  );
  const [guardianMobile, setGuardianMobile] = useState<string | undefined>(lead.personalDetails?.guardianMobile);
  const [guardianRelationship, setGuardianRelationship] = useState<string | undefined>(
    lead.personalDetails?.guardianRelationship
  );
  const [guardianName, setGuardianName] = useState(lead.personalDetails?.guardianName ?? '');
  const [dateOfBirth, setDateOfBirth] = useState<string>(lead.personalDetails?.dateOfBirth ?? '');
  const [mobile, setMobile] = useState<string>(lead?.mobile ?? '');
  const [email, setEmail] = useState(lead?.email ?? '');
  const [lastName, setLastName] = useState(lead?.lastName ?? '');
  const [firstName, setFirstName] = useState(lead?.firstName ?? '');
  const [isAgreeToBringGuardian, setIsAgreeToBringGuardian] = useState<'true' | 'false'>('false');
  const [errorList, setErrorList] = useState<string[]>([]);

  useEffect(() => {
    if (dateOfBirth) {
      setAge(moment().diff(moment(dateOfBirth, 'YYYY-MM-DD'), 'years'));
    }
  }, [dateOfBirth]);

  const validateFields = () => {
    const errors: string[] = [];

    if (!firstName || firstName.length === 0) {
      errors.push('firstName');
    }

    if (!lastName || lastName.length === 0) {
      errors.push('lastName');
    }

    if (!validateEmail(email)) {
      errors.push('email');
    }

    if (!validatePhone(mobile)) {
      errors.push('mobilePhone');
    }

    const isNotBetween100Years = !moment(dateOfBirth).isBetween(moment().subtract(100, 'years'), moment());
    const isNotValidDate = !moment(dateOfBirth, 'YYYY-MM-DD', true).isValid();

    if (isNotValidDate || isNotBetween100Years) {
      errors.push('dateOfBirth');
    }

    if (!gender) {
      errors.push('gender');
    }

    const memberAge = moment().diff(moment(dateOfBirth, 'YYYY-MM-DD'), 'years');
    if (memberAge < 21) {
      if (guardianName.length === 0) {
        errors.push('guardianName');
      }

      if (!validatePhone(guardianMobile)) {
        errors.push('guardianMobile');
      }

      if (!validateEmail(guardianEmail)) {
        errors.push('guardianEmail');
      }

      if (!guardianRelationship || guardianRelationship.length === 0) {
        errors.push('guardianRelationship');
      }

      if (!isAgreeToBringGuardian || isAgreeToBringGuardian === 'false') {
        errors.push('isAgreeToBringGuardian');
      }
    }

    if (errors.length > 0) {
      dispatch(actions.status.status({message: 'Please fill out the form.', type: StatusType.ERROR}));
    }

    setErrorList(errors);

    return errors.length === 0;
  };

  function onChange<T>(setter: Dispatch<SetStateAction<T>>, field?: string) {
    const res = onChangeHelper(setter);

    return (e: string | moment.Moment | React.ChangeEvent<HTMLInputElement>) => {
      if (field) {
        removeError(field);
      }

      res(e);
    };
  }

  const hasError = (field: string): boolean => {
    return errorList.includes(field);
  };

  const removeError = (field: string) => {
    setErrorList(errorList.filter((e) => e !== field));
  };

  const setters = {
    setDateOfBirth,
    setGender,
    setGuardianName,
    setGuardianRelationship,
    setGuardianMobile,
    setGuardianHomeNumber,
    setGuardianEmail,
    setGuardianMailingAddress,
    setIsAgreeToBringGuardian,
  };

  const personalDetails = {
    gender,
    age,
    dateOfBirth,
    guardianMailingAddress,
    guardianEmail,
    guardianHomeNumber,
    guardianMobile,
    guardianRelationship,
    guardianName,
    ageOfMajority: site.ageOfMajority,
    isAgreeToBringGuardian,
  };

  const onPreview = () => {
    if (!validateFields()) {
      return;
    }

    dispatch(actions.status.status());
    setView(ModalViewEnum.PREVIEW);
    setLead({firstName, lastName, email, mobile, personalDetails});
  };

  const getCommonClass = (field: string) => {
    return `form-control ${hasError(field) ? 'border border-danger' : ''}`;
  };

  return (
    <>
      <div className="card mb-3">
        <div className="card-body">
          <div className="h5 pt-2 pb-4 text-center">
            <span className="align-middle">Customer Profile</span>
          </div>
          <div>
            <div className="form-group row">
              <label className="col-sm-3 col-form-label">Name</label>
              <div className="col-sm-9 px-0 mx-0 row">
                <div className="col-sm-6 pr-sm-1">
                  <input
                    className={getCommonClass('firstName')}
                    type="text"
                    value={firstName}
                    onChange={onChange(setFirstName, 'firstName')}
                    placeholder="First Name"
                  />
                </div>
                <div className="col-sm-6 pt-3 pt-sm-0 pl-sm-1">
                  <input
                    className={getCommonClass('lastName')}
                    type="text"
                    value={lastName}
                    onChange={onChange(setLastName, 'lastName')}
                    placeholder="Last Name"
                  />
                </div>
              </div>
            </div>

            <div className="form-group row">
              <label className="col-sm-3 col-form-label">Mobile Phone:</label>
              <div className="col-sm-9">
                <Controls.PhoneNumberInput
                  isValid={!hasError('mobilePhone')}
                  number={mobile}
                  placeholder="Mobile Phone"
                  onChange={onChange(setMobile, 'mobilePhone')}
                />
              </div>
            </div>

            <div className="form-group row">
              <label className="col-sm-3 col-form-label">Email</label>
              <div className="col-sm-9">
                <input
                  className={getCommonClass('email')}
                  type="email"
                  value={email}
                  onChange={onChange(setEmail, 'email')}
                  placeholder="Email"
                />
              </div>
            </div>

            <PersonalDetailsEditFields
              fields={personalDetails}
              setters={setters}
              type={type}
              hasError={hasError}
              onChange={onChange}
            />
          </div>
        </div>
      </div>

      <div className="text-right">
        <button className="btn btn-primary" type="submit" onClick={onPreview}>
          Preview
        </button>
        <button className="btn btn-secondary ml-2" type="button" onClick={onCancel}>
          Cancel
        </button>
      </div>
    </>
  );
};
